import { useState, type Dispatch, type SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import posthog from 'posthog-js';
import {
  cancelListingMarketplaceCancelPost,
  getListingForHouseMarketplaceListingMapObjectIdGet,
  getRateMarketplaceRateGet,
  listHouseMarketplaceListPost,
} from '../../client';
import type { MarketplaceListing, MarketplaceRate } from '../../models/apiTypes';
import type { MapObject } from '../../models/mapObject';
import { showAlert, showConfirm } from '../../utils/telegram/alert';
import { handleApiError } from '../../utils/net/apiError';

export interface UseMarketplaceSellParams {
  plot: MapObject | null;
  queryClient: QueryClient;
  t: TFunction;
}

export interface UseMarketplaceSellResult {
  showSellModal: boolean;
  setShowSellModal: Dispatch<SetStateAction<boolean>>;
  sellPriceTon: string;
  setSellPriceTon: Dispatch<SetStateAction<string>>;
  sellPriceUsdt: string;
  setSellPriceUsdt: Dispatch<SetStateAction<string>>;
  sellEditingField: 'ton' | 'usdt';
  setSellEditingField: Dispatch<SetStateAction<'ton' | 'usdt'>>;
  sellRate: MarketplaceRate | null;
  sellLoading: boolean;
  activeListing: MarketplaceListing | null;
  listingLoading: boolean;
  showSellTooltip: boolean;
  setShowSellTooltip: Dispatch<SetStateAction<boolean>>;
  handleSellClick: () => Promise<void>;
  handleListForSale: () => Promise<void>;
  handleCancelListing: () => Promise<void>;
  handleSellTonChange: (val: string) => void;
  handleSellUsdtChange: (val: string) => void;
  handleSellQuickUsdt: (addUsdt: number) => void;
}

export function useMarketplaceSell({
  plot,
  queryClient,
  t,
}: UseMarketplaceSellParams): UseMarketplaceSellResult {
  const [showSellModal, setShowSellModal] = useState(false);
  const [sellPriceTon, setSellPriceTon] = useState('');
  const [sellPriceUsdt, setSellPriceUsdt] = useState('');
  const [sellEditingField, setSellEditingField] = useState<'ton' | 'usdt'>('ton');
  const [sellRate, setSellRate] = useState<MarketplaceRate | null>(null);
  const [sellLoading, setSellLoading] = useState(false);
  const [activeListing, setActiveListing] = useState<MarketplaceListing | null>(null);
  const [listingLoading, setListingLoading] = useState(false);
  const [showSellTooltip, setShowSellTooltip] = useState(false);

  // Marketplace sell handlers
  const handleSellClick = async () => {
    setShowSellModal(true);
    setListingLoading(true);
    posthog.capture('marketplace_sell_modal_opened', {
      house_id: plot?.id,
      real_estate_id: plot?.data?.real_estate_id,
      owner_id: plot?.owner_id,
    });
    try {
      const [rate, listing] = await Promise.all([
        (async () => {
          const { data } = await getRateMarketplaceRateGet({ throwOnError: true });
          return data;
        })(),
        plot ? (async () => {
          const { data, response } = await getListingForHouseMarketplaceListingMapObjectIdGet({
            path: { map_object_id: plot.id },
            throwOnError: false,
          });
          return response?.status === 200 ? (data ?? null) : null;
        })() : null,
      ]);
      setSellRate(rate);
      setActiveListing(listing);
    } catch (e) {
      console.error('[Marketplace] Failed to load sell data:', e);
    } finally {
      setListingLoading(false);
    }
  };

  const handleListForSale = async () => {
    if (!plot || !sellPriceUsdt || sellLoading) return;
    const price = parseFloat(sellPriceUsdt);
    if (isNaN(price) || price <= 0) {
      showAlert(t('Enter a valid price'));
      return;
    }
    setSellLoading(true);
    try {
      const { data: listing } = await listHouseMarketplaceListPost({
        body: { map_object_id: plot.id, price_usdt: price },
        throwOnError: true,
      });
      setActiveListing(listing);
      setSellPriceUsdt('');
      queryClient.invalidateQueries({ queryKey: ['marketplaceListings'] });
      posthog.capture('marketplace_house_listed', {
        house_id: plot.id,
        real_estate_id: plot.data?.real_estate_id,
        price_usdt: price,
        price_ton: listing.price_ton,
        memo: listing.memo,
        seller_id: listing.seller_id,
      });
      showAlert(t('House listed for sale!'));
    } catch (e: unknown) {
      const err = handleApiError(e, { logLabel: 'listHouseForSale' });
      posthog.capture('marketplace_house_list_failed', {
        house_id: plot.id,
        real_estate_id: plot.data?.real_estate_id,
        price_usdt: price,
        error: err.detail,
      });
      showAlert(err.detail || t('Failed to list house'));
    } finally {
      setSellLoading(false);
    }
  };

  const handleCancelListing = async () => {
    if (!activeListing || sellLoading) return;
    const confirmed = await showConfirm(t('Cancel this listing?'));
    if (!confirmed) return;
    setSellLoading(true);
    try {
      await cancelListingMarketplaceCancelPost({
        body: { listing_id: activeListing.id },
        throwOnError: true,
      });
      queryClient.invalidateQueries({ queryKey: ['marketplaceListings'] });
      posthog.capture('marketplace_listing_cancelled', {
        listing_id: activeListing.id,
        house_id: activeListing.map_object_id,
        price_usdt: activeListing.price_usdt,
        price_ton: activeListing.price_ton,
      });
      setActiveListing(null);
      showAlert(t('Listing cancelled'));
    } catch (e: unknown) {
      const err = handleApiError(e, { logLabel: 'cancelMarketplaceListing' });
      showAlert(err.detail || t('Failed to cancel listing'));
    } finally {
      setSellLoading(false);
    }
  };

  const sanitizeDecimalInput = (val: string): string =>
    val.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');

  const handleSellTonChange = (val: string) => {
    const clean = sanitizeDecimalInput(val);
    setSellPriceTon(clean);
    setSellEditingField('ton');
    if (clean && sellRate) {
      const ton = parseFloat(clean);
      if (!isNaN(ton)) setSellPriceUsdt((ton * sellRate.ton_usdt).toFixed(2));
      else setSellPriceUsdt('');
    } else {
      setSellPriceUsdt('');
    }
  };

  const handleSellUsdtChange = (val: string) => {
    const clean = sanitizeDecimalInput(val);
    setSellPriceUsdt(clean);
    setSellEditingField('usdt');
    if (clean && sellRate) {
      const usdt = parseFloat(clean);
      if (!isNaN(usdt)) setSellPriceTon((usdt / sellRate.ton_usdt).toFixed(4));
      else setSellPriceTon('');
    } else {
      setSellPriceTon('');
    }
  };

  const handleSellQuickUsdt = (addUsdt: number) => {
    const current = parseFloat(sellPriceUsdt) || 0;
    handleSellUsdtChange(String((current + addUsdt).toFixed(2)));
  };

  return {
    showSellModal,
    setShowSellModal,
    sellPriceTon,
    setSellPriceTon,
    sellPriceUsdt,
    setSellPriceUsdt,
    sellEditingField,
    setSellEditingField,
    sellRate,
    sellLoading,
    activeListing,
    listingLoading,
    showSellTooltip,
    setShowSellTooltip,
    handleSellClick,
    handleListForSale,
    handleCancelListing,
    handleSellTonChange,
    handleSellUsdtChange,
    handleSellQuickUsdt,
  };
}
